/**
 * Per-venue readiness for the Position Tracker (#904).
 *
 * The Tracker used to require the Kalshi connection for the whole page even
 * though the Polymarket readers ride their own relay endpoints. That left a
 * supported Poly-only account with no route to its own positions and orders,
 * and replaced the page during a Kalshi reconnect. The contract in
 * docs/260915TrackerVenueReadiness.md asks for explicit per-venue states,
 * independent admission of each permitted read surface, and honest coverage
 * labels on combined figures. This module is the state model; the view reads
 * it and never re-derives readiness from a boolean.
 *
 * Distinctions that must survive rendering:
 *  - `not-configured` (no credential / not in scope) is not `failed`
 *    (a credentialed read that did not succeed) and not `unauthorized`
 *    (the relay refused this account). Only the last two are "unreadable".
 *  - `stale` retains last-good rows with honest freshness; `loading` has
 *    nothing to show yet; `confirmed-empty` is real evidence of zero.
 *  - Nothing here grants an action. Execution routes keep their own
 *    entitlement and account checks.
 */
import type { AccountStatus } from '@/types/kalshi';
import type { PolyRelayUnavailableCode } from '@/lib/polymarket/relay';
import { KALSHI_PHASE_TEXT } from '@/lib/kalshiAccountPhase';

export type VenueReadinessState =
  | 'not-configured'
  | 'unauthorized'
  | 'loading'
  | 'ready'
  | 'confirmed-empty'
  | 'stale'
  | 'failed';

export interface VenueReadiness {
  state: VenueReadinessState;
  /** Rows from this venue may be displayed (possibly retained, see `state`). */
  readable: boolean;
  /** The latest completed read certifies the displayed rows. */
  confirmed: boolean;
  /** Short human explanation for the not-readable states; null when readable. */
  reason: string | null;
}

export interface KalshiReadinessInput {
  status: AccountStatus;
  error: string | null;
  credentialCount: number;
  /** The key check's own state (#1041): `unknown` until it answers, `error`
   * when it failed. Only a `checked` zero count means no keys. */
  credentialsStatus: 'unknown' | 'checked' | 'error';
  /** A failed connection still standing through the store's retry attempts
   * (`useKalshiAccountPhase`, #1060); null when none. */
  connectionFailure?: string | null;
  /** A connection is wanted (`accountStore.connectWanted`, #1060). */
  connectWanted?: boolean;
  positionsSync: { synced: boolean; lastSuccessMs: number | null; lastError: string | null; consecutiveFailures: number };
  positionCount: number;
  openOrderCount: number;
}

export interface PolyReadinessInput {
  status: 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';
  snapshotConfirmed: boolean;
  unavailableCode: PolyRelayUnavailableCode | null;
  error: string | null;
  positionCount: number;
}

const KALSHI_NOT_CONFIGURED =
  'Kalshi is not connected for this account. Ask your manager to add org keys, or add personal keys in Settings.';

const POLY_UNAVAILABLE_REASON: Record<PolyRelayUnavailableCode, string> = {
  NOT_IN_ORG: 'Polymarket reads need an organization account.',
  PERSONAL_KEY_MODE: 'Polymarket is unavailable in personal-key mode.',
  NO_POLY_CRED: 'Your organization has no Polymarket credential.',
  ONLY_LEGACY_EOA_CRED: 'Your organization’s Polymarket credential needs to be migrated before it can be read.',
};

const readable = (state: VenueReadinessState): boolean =>
  state === 'ready' || state === 'confirmed-empty' || state === 'stale';

function venue(state: VenueReadinessState, reason: string | null): VenueReadiness {
  return {
    state,
    readable: readable(state),
    confirmed: state === 'ready' || state === 'confirmed-empty',
    reason: readable(state) ? null : reason,
  };
}

/** Kalshi readiness from the account store. Connection status and positions
 * evidence are separate clocks: `connected` with a failing positions read is
 * not readable, and `connecting` with a prior snapshot is a reconnect that
 * keeps last-good rows on screen. */
export function kalshiReadiness(input: KalshiReadinessInput): VenueReadiness {
  const { status, error, credentialCount, positionsSync } = input;
  const hasPriorSnapshot = positionsSync.lastSuccessMs !== null;
  if (status === 'disconnected') {
    // #1060: before the key check answers, and when it fails, the count is 0
    // but nothing says the account has no keys. "Ask your manager to add org
    // keys" is only for a check that answered so.
    if (input.credentialsStatus === 'unknown') {
      return venue(hasPriorSnapshot ? 'stale' : 'loading', KALSHI_PHASE_TEXT.checking);
    }
    if (input.credentialsStatus === 'error') {
      return venue(hasPriorSnapshot ? 'stale' : 'failed', KALSHI_PHASE_TEXT['check-failed']);
    }
    if (credentialCount <= 0) return venue('not-configured', error ?? KALSHI_NOT_CONFIGURED);
    // Keys exist but the account is disconnected. With a connection still
    // wanted this is a session the store's self-heal watchdog reconnects (the
    // stale-generation strand after an account switch): loading. Without one
    // it is a deliberate disconnect in Settings, which nothing resolves on its
    // own (#1060: as loading it held back the notice, Cancel all and counts
    // indefinitely). It is unreadable, not excluded: the account still has its
    // orders and positions at Kalshi, so nothing may read as empty.
    if (input.connectWanted) return venue(hasPriorSnapshot ? 'stale' : 'loading', error ?? 'Connecting to Kalshi…');
    return venue(hasPriorSnapshot ? 'stale' : 'failed', error ?? KALSHI_PHASE_TEXT['not-connected']);
  }
  if (status === 'connecting') {
    // A retry after a failure is still a failure until it connects (#1060):
    // naming it as loading flipped it in and out of view on every attempt.
    if (input.connectionFailure) return venue(hasPriorSnapshot ? 'stale' : 'failed', input.connectionFailure);
    return venue(hasPriorSnapshot ? 'stale' : 'loading', error ?? 'Connecting to Kalshi…');
  }
  if (status === 'error') {
    // The latch's reason, as during the next attempt, so the line reads the
    // same through every retry instead of alternating with the long text.
    return venue(hasPriorSnapshot ? 'stale' : 'failed', input.connectionFailure ?? error ?? 'Kalshi connection failed.');
  }
  // connected
  if (positionsSync.synced && positionsSync.consecutiveFailures === 0) {
    return venue(input.positionCount + input.openOrderCount === 0 ? 'confirmed-empty' : 'ready', null);
  }
  if (hasPriorSnapshot) {
    return venue('stale', positionsSync.lastError ?? 'Kalshi positions refresh unconfirmed.');
  }
  return positionsSync.consecutiveFailures > 0
    ? venue('failed', positionsSync.lastError ?? 'Kalshi positions could not be read.')
    : venue('loading', 'Reading Kalshi positions…');
}

/** Polymarket readiness from the relay positions reader. The persisted ledger
 * is readable without trading credentials, so `unavailable` is a scope
 * problem on the read itself; a 401/403 clears rows and is `unauthorized`;
 * a transport failure retains rows as `stale` when there are any. */
export function polyReadiness(input: PolyReadinessInput): VenueReadiness {
  const { status, snapshotConfirmed, unavailableCode, error, positionCount } = input;
  if (status === 'unavailable') {
    return venue('not-configured', unavailableCode ? POLY_UNAVAILABLE_REASON[unavailableCode] : error ?? 'Polymarket is unavailable for this account.');
  }
  if (status === 'idle' || status === 'loading') {
    return venue('loading', 'Reading Polymarket positions…');
  }
  if (status === 'error') {
    if (/\b(401|403)\b|unauthori[sz]ed|forbidden/i.test(error ?? '')) {
      return venue('unauthorized', error ?? 'Polymarket refused this account.');
    }
    return positionCount > 0
      ? venue('stale', error ?? 'Polymarket positions refresh failed.')
      : venue('failed', error ?? 'Polymarket positions could not be read.');
  }
  // ready
  if (!snapshotConfirmed) return venue('stale', 'Polymarket positions awaiting confirmation.');
  return venue(positionCount === 0 ? 'confirmed-empty' : 'ready', null);
}

export interface UsReadinessInput {
  status: 'loading' | 'ready' | 'error';
  /** Accounts on the first page of the caller's visible US accounts. */
  accountCount: number;
  /** A further page exists, so an empty first page is not "no accounts". */
  hasMore: boolean;
  /** US client error code (`US_*`) when `status` is `error`. */
  error: string | null;
}

/** Polymarket US readiness from the caller's visible-account list (#1006).
 * The account list is the only US read the Tracker can make before an account
 * is chosen, so it decides whether the US section has anything to show. It is
 * not holdings evidence: US never enters the K/P combined figures here. A
 * login without an organization cannot form a US session at all, which is a
 * scope fact (`not-configured`), not a failed read. */
export function usReadiness(input: UsReadinessInput): VenueReadiness {
  const { status, accountCount, hasMore, error } = input;
  if (status === 'loading') return venue('loading', 'Reading Polymarket US accounts…');
  if (status === 'error') {
    return error === 'US_CLIENT_AUTH_REQUIRED'
      ? venue('not-configured', 'Polymarket US is not available for this login.')
      : venue('failed', `Polymarket US accounts could not be read (${error ?? 'US_READ_UNAVAILABLE'}).`);
  }
  return accountCount === 0 && !hasMore
    ? venue('not-configured', 'No Polymarket US accounts are available to your login.')
    : venue('ready', null);
}

export interface TrackerAdmission {
  /** Render the Tracker page (at least one venue can show or is about to). */
  admitted: boolean;
  /** Kalshi/international venues whose rows are on screen, for coverage
   * labels such as "K+P". US is admitted but not counted: its figures are not
   * part of the combined tiles. */
  coverage: 'K+P' | 'K' | 'P' | 'none';
}

/** The page renders when any venue is readable or still loading with a
 * configured credential. No venue configured, all unauthorized, or all
 * failed with nothing to show leaves the placeholder, which names each venue's
 * reason so the next step is visible. US admits only once readable: while its
 * account list is loading nothing yet says an account exists, and admitting
 * then would flash an empty page before the placeholder for every login with
 * no Kalshi or Polymarket read.
 *
 * `previous` is what the page showed on its last render (#1060). While a venue
 * that could still change the answer is loading, that choice stands: Kalshi
 * and Polymarket answering "unavailable" before US answered "readable" used to
 * swap the page for the placeholder and back. The answer changes only on
 * settled evidence: any venue readable, or none left loading. */
export function trackerAdmission(
  kalshi: VenueReadiness, poly: VenueReadiness, us?: VenueReadiness, previous?: boolean | null,
): TrackerAdmission {
  const pending = (v: VenueReadiness) => v.readable || v.state === 'loading';
  const anyReadable = kalshi.readable || poly.readable || us?.readable === true;
  const anyLoading = kalshi.state === 'loading' || poly.state === 'loading' || us?.state === 'loading';
  const admitted = anyReadable || (anyLoading && previous != null
    ? previous
    : pending(kalshi) || pending(poly));
  const coverage: TrackerAdmission['coverage'] =
    kalshi.readable && poly.readable ? 'K+P' : kalshi.readable ? 'K' : poly.readable ? 'P' : 'none';
  return { admitted, coverage };
}

export const VENUE_STATE_LABEL: Record<VenueReadinessState, string> = {
  'not-configured': 'not configured',
  unauthorized: 'not authorized',
  loading: 'loading',
  ready: 'live',
  'confirmed-empty': 'live (no holdings)',
  stale: 'reconnecting',
  failed: 'unavailable',
};
